import React from "react";
import { Link } from "react-router-dom";
import MainFooter from "../shared/MainFooter.jsx";
import { LEGAL_COMPANY } from "@/lib/legalCompany";

const LegalPageShell = ({ title, children }) => (
  <div className="choice-landing min-h-screen bg-[#080b14] noise-overlay">
    <header className="border-b border-white/[0.05]">
      <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img src="/images/logo.png" alt={LEGAL_COMPANY.brandName} className="w-full max-w-[100px]" />
        </Link>
        <Link to="/" className="text-sm text-gray-400 hover:text-white transition-colors">
          ← Back to home
        </Link>
      </div>
    </header>

    <main className="max-w-3xl mx-auto px-6 py-16">
      <h1 className="text-3xl md:text-4xl font-semibold text-white mb-8">{title}</h1>
      <div className="prose prose-invert prose-sm max-w-none text-gray-400 space-y-6">{children}</div>
    </main>

    <MainFooter />
  </div>
);

const linkClass = "text-teal-400 hover:text-teal-300";

const DpaPage = () => (
  <LegalPageShell title="Data Processing Agreement">
    <p className="text-gray-500 text-sm">Last updated: August 2026</p>

    <section className="space-y-4">
      <p>
        This Data Processing Agreement (&quot;DPA&quot;) forms part of the {LEGAL_COMPANY.brandName}{" "}
        <Link to="/terms" className={linkClass}>
          Terms of Service
        </Link>{" "}
        between:
      </p>
      <p>
        <strong className="text-white">Processor:</strong> {LEGAL_COMPANY.legalName} (ACN: {LEGAL_COMPANY.acn}; ABN: {LEGAL_COMPANY.abn}), of {LEGAL_COMPANY.address} (&quot;{LEGAL_COMPANY.brandName},&quot; &quot;Processor,&quot;
        &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;)
      </p>
      <p>
        and
      </p>
      <p>
        <strong className="text-white">Controller:</strong> the Customer entity that accepts the Terms of Service and
        uses the Service (&quot;Customer,&quot; &quot;Controller,&quot; &quot;you,&quot; or &quot;your&quot;).
      </p>
      <p>
        This DPA applies to the extent {LEGAL_COMPANY.brandName} processes Personal Data on Customer&apos;s behalf in connection with
        the Service. By creating an Account, accepting the Terms, or using the Service in a manner that causes
        {LEGAL_COMPANY.brandName} to process Personal Data on your behalf, you agree to this DPA. You may request a signed copy by
        emailing{" "}
        <a href={`mailto:${LEGAL_COMPANY.supportEmail}`} className={linkClass}>
          {LEGAL_COMPANY.supportEmail}
        </a>
        .
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">1. Definitions</h2>
      <p>Capitalised terms not defined in this DPA have the meaning given in the Terms of Service.</p>
      <p>
        <strong className="text-white">&quot;Applicable Data Protection Law&quot;</strong> means all privacy and data
        protection laws applicable to the processing of Personal Data under this DPA, including (as applicable) the
        Australian Privacy Act 1988 (Cth) and Australian Privacy Principles (APPs), the New Zealand Privacy Act 2020, the
        EU/UK GDPR, and the CCPA/CPRA.
      </p>
      <p>
        <strong className="text-white">&quot;Personal Data&quot;</strong> means personal information or personal data as
        defined under Applicable Data Protection Law that {LEGAL_COMPANY.brandName} processes on Customer&apos;s behalf in providing
        the Service.
      </p>
      <p>
        <strong className="text-white">&quot;Processing&quot; / &quot;process&quot;</strong> have the meanings under
        Applicable Data Protection Law.
      </p>
      <p>
        <strong className="text-white">&quot;Security Incident&quot;</strong> means a breach of security leading to the
        accidental or unlawful destruction, loss, alteration, unauthorised disclosure of, or access to, Personal Data
        processed by {LEGAL_COMPANY.brandName} on Customer&apos;s behalf.
      </p>
      <p>
        <strong className="text-white">&quot;Service&quot;</strong> means the {LEGAL_COMPANY.brandName} services described in the Terms.
      </p>
      <p>
        <strong className="text-white">&quot;Subprocessor&quot;</strong> means a third party engaged by {LEGAL_COMPANY.brandName} to
        process Personal Data on behalf of Customer in connection with the Service.
      </p>
      <p>
        <strong className="text-white">&quot;Terms&quot;</strong> means the {LEGAL_COMPANY.brandName} Terms of Service available at{" "}
        <Link to="/terms" className={linkClass}>
          /terms
        </Link>
        .
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">2. Roles of the Parties</h2>
      <p>
        2.1 Customer is the Controller (or equivalent, including &quot;business&quot; under CCPA/CPRA where applicable)
        of Personal Data that Customer uploads to, collects through, or causes to be processed by the Service (including
        lead and end-user data).
      </p>
      <p>
        2.2 {LEGAL_COMPANY.brandName} is the Processor (or &quot;service provider&quot; / &quot;contractor&quot; under CCPA/CPRA where
        applicable) and will process Personal Data only on Customer&apos;s documented instructions, including as set out
        in the Terms, this DPA, and Customer&apos;s use of the Service configurations.
      </p>
      <p>
        2.3 Customer is responsible for the lawfulness of its instructions, for providing any required notices to data
        subjects, and for obtaining any required consents for Customer&apos;s collection and use of Personal Data
        (including communications consent for SMS, email, and chat).
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">3. Subject Matter, Nature, Purpose &amp; Duration</h2>
      <p>
        3.1 <strong className="text-white">Subject matter:</strong> Processing of Personal Data in connection with
        Customer&apos;s use of the Service (AI-powered automation, lead communications, calendar booking, and related
        features).
      </p>
      <p>
        3.2 <strong className="text-white">Nature and purpose:</strong> Hosting, storage, transmission, analysis,
        generation of AI outputs, message delivery, scheduling, logging, security, support, and improvement of the Service
        for Customer, as configured by Customer.
      </p>
      <p>
        3.3 <strong className="text-white">Duration:</strong> For the term of Customer&apos;s Account and thereafter as
        required for deletion/return under this DPA and the Terms (including retention exceptions for legal, billing,
        security, and backup purposes).
      </p>
      <p>
        3.4 <strong className="text-white">Categories of data subjects:</strong> Customer&apos;s leads, prospects,
        customers, and other end users with whom Customer communicates or whose data Customer uploads; and Customer&apos;s
        authorised users of the Service, as applicable.
      </p>
      <p>
        3.5 <strong className="text-white">Types of Personal Data:</strong> As configured by Customer, which may include
        names, contact details (email, phone), addresses, message content and metadata, appointment details, CRM
        identifiers, and other information Customer chooses to process through the Service. Customer must not instruct
        {LEGAL_COMPANY.brandName} to process special-category or sensitive data unless expressly agreed in writing and permitted by
        Applicable Data Protection Law.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">4. Processor Obligations</h2>
      <p>
        4.1 <strong className="text-white">Instructions.</strong> {LEGAL_COMPANY.brandName} will process Personal Data only on
        documented instructions from Customer, unless required to do otherwise by applicable law (in which case
        {LEGAL_COMPANY.brandName} will, where legally permitted, inform Customer before processing).
      </p>
      <p>
        4.2 <strong className="text-white">Confidentiality.</strong> {LEGAL_COMPANY.brandName} will ensure that persons authorised to
        process Personal Data are bound by confidentiality obligations.
      </p>
      <p>
        4.3 <strong className="text-white">Security.</strong> {LEGAL_COMPANY.brandName} will implement appropriate technical and
        organisational measures to protect Personal Data, taking into account the state of the art, costs, and the
        nature, scope, context, and purposes of processing. Measures may include access controls, encryption in transit,
        logging, vulnerability management, and employee security awareness, as further described in the{" "}
        <Link to="/privacy-policy" className={linkClass}>
          Privacy Policy
        </Link>{" "}
        or security documentation {LEGAL_COMPANY.brandName} makes available.
      </p>
      <p>
        4.4 <strong className="text-white">Assistance — data subject rights.</strong> Taking into account the nature of
        processing, {LEGAL_COMPANY.brandName} will provide reasonable assistance to Customer, via appropriate technical and organisational
        measures, to enable Customer to respond to requests from data subjects to exercise rights under Applicable Data
        Protection Law.
      </p>
      <p>
        4.5 <strong className="text-white">Assistance — compliance.</strong> {LEGAL_COMPANY.brandName} will provide reasonable assistance
        to Customer in meeting Customer&apos;s obligations regarding security, Security Incident notifications, and data
        protection impact assessments, to the extent related to {LEGAL_COMPANY.brandName}&apos; processing and information available
        to {LEGAL_COMPANY.brandName}.
      </p>
      <p>
        4.6 <strong className="text-white">Security Incidents.</strong> {LEGAL_COMPANY.brandName} will notify Customer without undue delay
        after becoming aware of a Security Incident affecting Personal Data processed on Customer&apos;s behalf, and will
        provide information reasonably required for Customer to meet its notification obligations. {LEGAL_COMPANY.brandName}&apos;
        notification is not an admission of fault or liability.
      </p>
      <p>
        4.7 <strong className="text-white">Deletion and return.</strong> Upon termination of the Service or upon
        Customer&apos;s written request, {LEGAL_COMPANY.brandName} will delete or return Personal Data in accordance with the Terms
        (including any export window), except where retention is required by law or permitted retention exceptions in
        the Terms (billing, legal hold, security/audit logs, backups).
      </p>
      <p>
        4.8 <strong className="text-white">Audits.</strong> Upon reasonable written notice (not more than once per calendar
        year, except following a Security Incident or regulatory request), Customer may audit {LEGAL_COMPANY.brandName}&apos;
        compliance with this DPA by: (a) reviewing available security documentation and questionnaire responses; and/or
        (b) a remote audit mutually scheduled during business hours. On-site audits require {LEGAL_COMPANY.brandName}&apos; prior
        written consent and are at Customer&apos;s expense unless a material breach of this DPA is confirmed. {LEGAL_COMPANY.brandName}
        may require a reasonable confidentiality agreement and may redact information that would compromise security or
        third-party confidentiality.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">5. Subprocessors</h2>
      <p>
        5.1 Customer provides general authorisation for {LEGAL_COMPANY.brandName} to engage Subprocessors to process Personal Data as
        needed to provide the Service.
      </p>
      <p>
        5.2 {LEGAL_COMPANY.brandName} will impose data protection obligations on Subprocessors that are no less protective in substance
        than those in this DPA, to the extent applicable to the Subprocessor&apos;s services.
      </p>
      <p>5.3 Illustrative Subprocessors (as of the Last Updated date) include:</p>
      <ul className="list-disc pl-5 space-y-2">
        <li>Cloud hosting / infrastructure (e.g., Amazon Web Services)</li>
        <li>Payment processing (e.g., Stripe)</li>
        <li>Enterprise AI model providers (e.g., OpenAI, Anthropic)</li>
        <li>CRM, messaging, email, or calendar integrations configured or enabled by Customer</li>
      </ul>
      <p>
        5.4 {LEGAL_COMPANY.brandName} will provide notice of intended changes to material Subprocessors (including via email, in-product
        notice, or update to this DPA / Subprocessor list) and give Customer a reasonable opportunity to object on
        legitimate data-protection grounds. If Customer reasonably objects and the parties cannot resolve the objection,
        Customer may terminate the affected Service as its sole remedy. A current list is also available on request at{" "}
        <a href={`mailto:${LEGAL_COMPANY.supportEmail}`} className={linkClass}>
          {LEGAL_COMPANY.supportEmail}
        </a>
        .
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">6. International Transfers</h2>
      <p>
        6.1 Customer acknowledges that {LEGAL_COMPANY.brandName} and its Subprocessors may process Personal Data in Australia, the United
        States, and other countries where Subprocessors operate.
      </p>
      <p>
        6.2 Where Applicable Data Protection Law requires a transfer mechanism (including APP 8, New Zealand Information
        Privacy Principle 12, or GDPR Chapter V), {LEGAL_COMPANY.brandName} will implement appropriate safeguards (such as standard
        contractual clauses, adequacy decisions, or other lawful mechanisms) as required.
      </p>
      <p>
        6.3 Customer authorises such transfers as necessary to provide the Service and instructs {LEGAL_COMPANY.brandName} to enter into
        transfer mechanisms on Customer&apos;s behalf where required for Subprocessor transfers.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">7. CCPA/CPRA (California) — Service Provider Terms</h2>
      <p>Where CCPA/CPRA applies:</p>
      <p>
        7.1 {LEGAL_COMPANY.brandName} is a &quot;service provider&quot; and/or &quot;contractor&quot; and will process Personal Information
        only for the business purposes of providing the Service under the Terms and this DPA.
      </p>
      <p>
        7.2 {LEGAL_COMPANY.brandName} will not: (a) sell Personal Information; (b) share Personal Information for cross-context
        behavioural advertising; (c) retain, use, or disclose Personal Information for any purpose other than the
        business purposes specified in the Terms and this DPA, or as otherwise permitted by CCPA/CPRA; or (d) combine
        Personal Information with personal information from other sources except as permitted for a service
        provider/contractor.
      </p>
      <p>
        7.3 {LEGAL_COMPANY.brandName} will comply with applicable CCPA/CPRA obligations for service providers/contractors and will notify
        Customer if {LEGAL_COMPANY.brandName} determines it can no longer meet those obligations.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">8. Customer&apos;s Instructions &amp; Warranties</h2>
      <p>
        Customer warrants that: (a) it has a lawful basis (or equivalent) to process and instruct {LEGAL_COMPANY.brandName} to process
        Personal Data; (b) its instructions will not cause {LEGAL_COMPANY.brandName} to breach Applicable Data Protection Law; and (c) it
        will not use the Service to process Personal Data in violation of communications, telemarketing, or spam laws.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">9. Liability</h2>
      <p>
        Each party&apos;s liability under this DPA is subject to the limitations and exclusions in the Terms, to the
        maximum extent permitted by Applicable Data Protection Law. Nothing in this DPA limits liability that cannot be
        limited under applicable law.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">10. Order of Precedence</h2>
      <p>
        If there is a conflict between this DPA and the Terms regarding processing of Personal Data, this DPA (available
        at /dpa) prevails. If a signed copy of this DPA exists, that signed copy controls over the online DPA only where
        they conflict on the same topic. Commercial terms in a signed Order Form prevail for pricing, term, and scope
        as stated in the Terms&apos; order of precedence. The Privacy Policy describes {LEGAL_COMPANY.brandName}&apos; own collection
        practices as a business; this DPA governs processing on Customer&apos;s behalf.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">11. Changes to this DPA</h2>
      <p>
        {LEGAL_COMPANY.brandName} may update this DPA upon notice consistent with the Terms&apos; change process for material changes.
        If a material change adversely affects Customer&apos;s rights under Applicable Data Protection Law, Customer may
        terminate the affected Service before the change takes effect as provided in the Terms.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">12. Governing Law &amp; Disputes</h2>
      <p>
        This DPA is governed by the laws of {LEGAL_COMPANY.governingLaw}, without regard to conflict of law rules. Disputes
        under this DPA are resolved under the dispute resolution provisions of the Terms.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">13. Contact</h2>
      <div className="pl-4 border-l border-white/10 space-y-1">
        <p>{LEGAL_COMPANY.legalName}</p>
        <p>ACN: {LEGAL_COMPANY.acn}</p>
        <p>ABN: {LEGAL_COMPANY.abn}</p>
        <p>{LEGAL_COMPANY.address}</p>
        <p>
          Email:{" "}
          <a href={`mailto:${LEGAL_COMPANY.supportEmail}`} className={linkClass}>
            {LEGAL_COMPANY.supportEmail}
          </a>
        </p>
      </div>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">14. Acceptance</h2>
      <p>
        Electronic acceptance of the Terms, creation of an Account, or continued use of the Service that involves
        processing of Personal Data on Customer&apos;s behalf constitutes Customer&apos;s agreement to this DPA. A
        countersigned PDF may be requested from{" "}
        <a href={`mailto:${LEGAL_COMPANY.supportEmail}`} className={linkClass}>
          {LEGAL_COMPANY.supportEmail}
        </a>
        .
      </p>
    </section>
  </LegalPageShell>
);

export default DpaPage;
